import React from 'react';
import { Send, Bell, BookOpen, ShoppingBag, Wallet, SearchCheck, Image, Plus, Trash2 } from 'lucide-react';
import type { BlockRenderProps } from './types';
import { EditableText } from './EditableText';

export const ConversionFormBlockRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const title = (node.props?.title as string) || (node.props?.formTitle as string) || 'Request Free Counseling & Demo Class';
  const subtitle = (node.props?.subtitle as string) || 'Fill out your details below and our academic counselor will contact you within 24 hours.';
  const submitButtonText = (node.props?.submitButtonText as string) || (node.props?.submitButtonLabel as string) || 'Submit Inquiry';

  return (
    <div className="max-w-md mx-auto rounded-2xl border border-border bg-card p-6 shadow-sm">
      <div className="text-center mb-5">
        <div className="inline-flex p-2.5 rounded-full bg-primary/10 text-primary mb-2">
          <Send className="h-5 w-5" />
        </div>
        <EditableText
          as="h3"
          value={title}
          isEditor={isEditor}
          onChange={(newVal) => onUpdateProps?.(node.id, { title: newVal, formTitle: newVal })}
          className="text-lg font-bold text-foreground block"
        />
        <EditableText
          as="p"
          value={subtitle}
          isEditor={isEditor}
          onChange={(newVal) => onUpdateProps?.(node.id, { subtitle: newVal })}
          className="text-xs text-muted-foreground mt-1 block"
        />
      </div>

      <form className="space-y-3" onSubmit={(e) => e.preventDefault()}>
        <div>
          <label className="text-xs font-semibold text-foreground block mb-1">Student Full Name</label>
          <input
            type="text"
            placeholder="e.g. Rahul Sharma"
            className="w-full px-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            readOnly
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-foreground block mb-1">Mobile / WhatsApp Number</label>
          <input
            type="tel"
            placeholder="e.g. +91 98765 43210"
            className="w-full px-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            readOnly
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-foreground block mb-1">Target Exam / Grade</label>
          <select className="w-full px-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary">
            <option>JEE Main &amp; Advanced</option>
            <option>NEET Medical</option>
            <option>Foundation Class 9-10</option>
            <option>Class 11-12 Boards</option>
          </select>
        </div>
        <button
          type="button"
          className="w-full py-2.5 mt-2 rounded-lg bg-primary text-primary-foreground font-semibold text-sm shadow hover:opacity-90 transition-opacity"
        >
          {submitButtonText}
        </button>
      </form>
    </div>
  );
};

export const SchoolNoticeBoardBlockRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const title = (node.props?.title as string) || 'Latest Notices & Circulars';

  const notices: any[] = Array.isArray(node.props?.items) ? node.props.items : [];

  const updateNotice = (idx: number, field: string, val: string) => {
    const updated = [...notices];
    updated[idx] = { ...updated[idx], [field]: val };
    onUpdateProps?.(node.id, { items: updated });
  };

  const removeNotice = (idx: number) => {
    const updated = notices.filter((_, i) => i !== idx);
    onUpdateProps?.(node.id, { items: updated });
  };

  const addNotice = () => {
    const newNotice = { title: 'New Academic Notice', date: 'Today', tag: 'General' };
    onUpdateProps?.(node.id, { items: [...notices, newNotice] });
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm my-4 w-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <EditableText
            as="h3"
            value={title}
            isEditor={isEditor}
            onChange={(newVal) => onUpdateProps?.(node.id, { title: newVal })}
            className="font-bold text-base text-foreground inline-block"
          />
        </div>
        {isEditor && (
          <button
            type="button"
            onClick={addNotice}
            className="text-xs px-2.5 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground font-semibold flex items-center gap-1 transition-colors"
          >
            <Plus className="h-3 w-3" />
            <span>Add Notice</span>
          </button>
        )}
      </div>

      {notices.length === 0 ? (
        <div className="py-6 text-center text-xs text-muted-foreground border border-dashed border-border rounded-lg">
          {isEditor ? 'No notices added yet. Click "Add Notice" above to create an announcement.' : 'No notices published yet.'}
        </div>
      ) : (
        <div className="divide-y divide-border">
          {notices.map((n, i) => (
            <div key={i} className="py-2.5 first:pt-0 last:pb-0 flex items-center justify-between gap-3 group/notice">
              <div className="flex-1">
                <EditableText
                  as="div"
                  value={n.title}
                  isEditor={isEditor}
                  onChange={(val) => updateNotice(i, 'title', val)}
                  className="text-sm font-medium text-foreground hover:text-primary transition-colors block"
                />
                <EditableText
                  as="div"
                  value={n.date}
                  isEditor={isEditor}
                  onChange={(val) => updateNotice(i, 'date', val)}
                  className="text-xs text-muted-foreground mt-0.5 block"
                />
              </div>
              <div className="flex items-center gap-2">
                <EditableText
                  as="span"
                  value={n.tag}
                  isEditor={isEditor}
                  onChange={(val) => updateNotice(i, 'tag', val)}
                  className="text-[10px] font-semibold px-2 py-0.5 rounded bg-muted text-muted-foreground block"
                />
                {isEditor && (
                  <button
                    type="button"
                    onClick={() => removeNotice(i)}
                    className="opacity-0 group-hover/notice:opacity-100 p-1 text-rose-500 hover:bg-rose-500/10 rounded transition-all"
                    title="Remove notice"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const CoachCourseCatalogBlockRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const title = (node.props?.title as string) || (node.props?.text as string) || 'Our Popular Entrance Batches';

  const courses: any[] = Array.isArray(node.props?.items) ? node.props.items : [];

  const updateCourse = (idx: number, field: string, val: string) => {
    const updated = [...courses];
    updated[idx] = { ...updated[idx], [field]: val };
    onUpdateProps?.(node.id, { items: updated });
  };

  const removeCourse = (idx: number) => {
    const updated = courses.filter((_, i) => i !== idx);
    onUpdateProps?.(node.id, { items: updated });
  };

  const addCourse = () => {
    const newCourse = {
      name: 'Foundation Science & Math Batch',
      duration: '1 Year',
      rating: '4.9 ★',
      badge: 'New Batch',
      ctaText: 'View Syllabus & Enroll',
    };
    onUpdateProps?.(node.id, { items: [...courses, newCourse] });
  };

  return (
    <div className="py-6 w-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          <EditableText
            as="h3"
            value={title}
            isEditor={isEditor}
            onChange={(newVal) => onUpdateProps?.(node.id, { title: newVal, text: newVal })}
            className="text-xl font-bold text-foreground inline-block"
          />
        </div>
        {isEditor && (
          <button
            type="button"
            onClick={addCourse}
            className="text-xs px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add Course Card</span>
          </button>
        )}
      </div>

      {courses.length === 0 ? (
        <div className="py-8 text-center text-xs text-muted-foreground border border-dashed border-border rounded-xl">
          {isEditor ? 'No courses added yet. Click "Add Course Card" above to showcase a course.' : 'No courses published yet.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {courses.map((c, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4 shadow-sm flex flex-col justify-between relative group/course hover:shadow-md transition-shadow">
            {isEditor && (
              <button
                type="button"
                onClick={() => removeCourse(i)}
                className="absolute top-2 right-2 opacity-0 group-course:opacity-100 p-1 text-rose-500 hover:bg-rose-500/10 rounded transition-all cursor-pointer"
                title="Remove course card"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}

            <div>
              <EditableText
                as="span"
                value={c.badge}
                isEditor={isEditor}
                onChange={(val) => updateCourse(i, 'badge', val)}
                className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary uppercase inline-block"
              />
              <EditableText
                as="h4"
                value={c.name}
                isEditor={isEditor}
                onChange={(val) => updateCourse(i, 'name', val)}
                className="font-bold text-base text-foreground mt-2 block"
              />
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                <span>Duration:</span>
                <EditableText
                  as="span"
                  value={c.duration}
                  isEditor={isEditor}
                  onChange={(val) => updateCourse(i, 'duration', val)}
                  className="font-medium"
                />
                <span>•</span>
                <EditableText
                  as="span"
                  value={c.rating}
                  isEditor={isEditor}
                  onChange={(val) => updateCourse(i, 'rating', val)}
                  className="text-amber-500 font-semibold"
                />
              </div>
            </div>

            <button
              type="button"
              className="mt-4 w-full py-1.5 rounded-lg border border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold text-xs transition-colors"
            >
              <EditableText
                as="span"
                value={c.ctaText || 'View Syllabus & Enroll'}
                isEditor={isEditor}
                onChange={(val) => updateCourse(i, 'ctaText', val)}
              />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);
};

export const AppProductCatalogBlockRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const title = (node.props?.title as string) || 'Official Institute Merchandise & Study Kits';

  return (
    <div className="py-6 w-full">
      <div className="flex items-center gap-2 mb-4">
        <ShoppingBag className="h-5 w-5 text-primary" />
        <EditableText
          as="h3"
          value={title}
          isEditor={isEditor}
          onChange={(newVal) => onUpdateProps?.(node.id, { title: newVal })}
          className="text-xl font-bold text-foreground inline-block"
        />
      </div>
      <div className="p-8 border border-dashed border-border rounded-xl text-center text-sm text-muted-foreground">
        Live inventory catalog items will populate automatically from Suite S8 Ops.
      </div>
    </div>
  );
};

export const FeePaymentPortalBlockRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const title = (node.props?.title as string) || 'Online Fee Payment & Installments';

  return (
    <div className="max-w-md mx-auto rounded-xl border border-border bg-card p-5 shadow-sm my-4 text-center">
      <div className="inline-flex p-2.5 rounded-full bg-primary/10 text-primary mb-2">
        <Wallet className="h-5 w-5" />
      </div>
      <EditableText
        as="h3"
        value={title}
        isEditor={isEditor}
        onChange={(newVal) => onUpdateProps?.(node.id, { title: newVal })}
        className="font-bold text-base text-foreground mb-1 block"
      />
      <p className="text-xs text-muted-foreground mb-4">
        Enter student roll number or enrollment ID to check due fee installments and pay online securely.
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Student Roll No / ID"
          className="flex-1 px-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          readOnly
        />
        <button
          type="button"
          className="px-4 py-2 bg-primary text-primary-foreground font-semibold text-xs rounded-lg hover:opacity-90"
        >
          Check Dues
        </button>
      </div>
    </div>
  );
};

export const AdmissionsStatusTrackerBlockRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const title = (node.props?.title as string) || 'Check Admission Application Status';

  return (
    <div className="max-w-md mx-auto rounded-xl border border-border bg-card p-5 shadow-sm my-4 text-center">
      <div className="inline-flex p-2.5 rounded-full bg-primary/10 text-primary mb-2">
        <SearchCheck className="h-5 w-5" />
      </div>
      <EditableText
        as="h3"
        value={title}
        isEditor={isEditor}
        onChange={(newVal) => onUpdateProps?.(node.id, { title: newVal })}
        className="font-bold text-base text-foreground mb-1 block"
      />
      <p className="text-xs text-muted-foreground mb-4">
        Enter your application token or registered phone number to track your seat allotment and document verification.
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Application Token / Phone"
          className="flex-1 px-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          readOnly
        />
        <button
          type="button"
          className="px-4 py-2 bg-primary text-primary-foreground font-semibold text-xs rounded-lg hover:opacity-90"
        >
          Track Status
        </button>
      </div>
    </div>
  );
};

export const ImageGalleryRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const title = (node.props?.title as string) || 'Campus Infrastructure & Labs';

  const defaultImages = [
    { url: 'https://images.unsplash.com/photo-1562774053-701939374585?w=600&auto=format&fit=crop&q=80', caption: 'High-Tech Physics & Chemistry Labs' },
    { url: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=600&auto=format&fit=crop&q=80', caption: 'Smart Digital Classrooms' },
    { url: 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=600&auto=format&fit=crop&q=80', caption: 'Quiet 24/7 Library & Reading Rooms' },
  ];

  const images: any[] = Array.isArray(node.props?.images) && node.props.images.length > 0
    ? node.props.images
    : defaultImages;

  return (
    <div className="py-6 w-full">
      <div className="flex items-center gap-2 mb-4">
        <Image className="h-5 w-5 text-primary" />
        <EditableText
          as="h3"
          value={title}
          isEditor={isEditor}
          onChange={(newVal) => onUpdateProps?.(node.id, { title: newVal })}
          className="text-xl font-bold text-foreground inline-block"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {images.map((img, i) => (
          <div key={i} className="rounded-xl overflow-hidden border border-border shadow-xs group">
            <div className="h-48 overflow-hidden bg-muted">
              <img
                src={img.url}
                alt={img.caption}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </div>
            <div className="p-3 bg-card">
              <div className="text-xs font-medium text-foreground">{img.caption}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
